import { ApiConfig } from './config';
import { handleResponse } from './config';

// Define setting types
export type Setting = {
  _id: string;
  name: string;
  value: any;
  category: string;
  description?: string;
  dataType: 'string' | 'number' | 'boolean' | 'object' | 'array';
  defaultValue?: any;
  updatedAt: string;
  displayValue?: string; // For formatted display
};

// Group settings by categories
export type GroupedSettings = Record<string, Setting[]>;

export class SettingsService {
  constructor(private config: ApiConfig) {}

  // Get all settings (optionally filtered by category)
  async getAllSettings(category?: string): Promise<GroupedSettings> {
    const queryParams = category ? `?category=${category}` : '';
    const response = await fetch(`${this.config.baseUrl}/settings${queryParams}`, {
      headers: this.config.headers
    });
    return handleResponse<GroupedSettings>(response);
  }

  // Get a specific setting by name
  async getSetting(name: string): Promise<Setting> {
    const response = await fetch(`${this.config.baseUrl}/settings/${name}`, {
      headers: this.config.headers
    });
    return handleResponse<Setting>(response);
  }

  // Create a new setting
  async createSetting(settingData: Omit<Setting, '_id' | 'updatedAt'>): Promise<Setting> {
    const response = await fetch(`${this.config.baseUrl}/settings`, {
      method: 'POST',
      headers: this.config.headers,
      body: JSON.stringify(settingData)
    });
    return handleResponse<Setting>(response);
  }

  // Update a setting
  async updateSetting(name: string, value: any, additionalData?: Partial<Setting>): Promise<Setting> {
    const payload = {
      value,
      ...additionalData
    };
    
    const response = await fetch(`${this.config.baseUrl}/settings/${name}`, {
      method: 'PUT',
      headers: this.config.headers,
      body: JSON.stringify(payload)
    });
    return handleResponse<Setting>(response);
  }

  // Delete a setting
  async deleteSetting(name: string): Promise<{ message: string }> {
    const response = await fetch(`${this.config.baseUrl}/settings/${name}`, {
      method: 'DELETE',
      headers: this.config.headers
    });
    return handleResponse<{ message: string }>(response);
  }

  // Reset a setting to its default value
  async resetSetting(name: string): Promise<Setting> {
    const response = await fetch(`${this.config.baseUrl}/settings/${name}/reset`, {
      method: 'POST',
      headers: this.config.headers
    });
    return handleResponse<Setting>(response);
  }

  // Batch update multiple settings
  async batchUpdateSettings(settings: { name: string; value: any }[]): Promise<{
    success: boolean;
    updated: Setting[];
    errors?: { name: string; error: string }[];
  }> {
    const response = await fetch(`${this.config.baseUrl}/settings`, {
      method: 'PUT',
      headers: this.config.headers,
      body: JSON.stringify({ settings })
    });
    return handleResponse<{
      success: boolean;
      updated: Setting[];
      errors?: { name: string; error: string }[];
    }>(response);
  }

  // Export settings (for backup)
  async exportSettings(): Promise<{
    success: boolean;
    count: number;
    exportDate: string;
    settings: Setting[];
  }> {
    const response = await fetch(`${this.config.baseUrl}/admin/settings/export`, {
      headers: this.config.headers
    });
    return handleResponse<{
      success: boolean;
      count: number;
      exportDate: string;
      settings: Setting[];
    }>(response);
  }

  // Import settings (from backup)
  async importSettings(settings: Partial<Setting>[]): Promise<{
    success: boolean;
    stats: {
      created: number;
      updated: number;
      skipped: number;
      failed: number;
    };
    message: string;
  }> {
    const response = await fetch(`${this.config.baseUrl}/admin/settings/import`, {
      method: 'POST',
      headers: this.config.headers,
      body: JSON.stringify({ settings })
    });
    return handleResponse<{
      success: boolean;
      stats: {
        created: number;
        updated: number;
        skipped: number;
        failed: number;
      };
      message: string;
    }>(response);
  }

  // Initialize default settings
  async initializeSettings(): Promise<{
    success: boolean;
    stats: {
      created: number;
      updated: number;
      unchanged: number;
      failed: number;
    };
    message: string;
  }> {
    const response = await fetch(`${this.config.baseUrl}/admin/settings/initialize`, {
      method: 'POST',
      headers: this.config.headers
    });
    return handleResponse<{
      success: boolean;
      stats: {
        created: number;
        updated: number;
        unchanged: number;
        failed: number;
      };
      message: string;
    }>(response);
  }

  // Clear settings cache
  async clearCache(): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetch(`${this.config.baseUrl}/admin/settings/clear-cache`, {
      method: 'POST',
      headers: this.config.headers
    });
    return handleResponse<{
      success: boolean;
      message: string;
    }>(response);
  }

  // Get settings change history
  async getSettingsHistory(settingName?: string, limit = 50, skip = 0): Promise<{
    history: Array<{
      _id: string;
      settingName: string;
      action: 'create' | 'update' | 'delete' | 'reset';
      previousValue?: any;
      newValue?: any;
      category?: string;
      updatedBy?: string;
      user?: {
        _id: string;
        name: string;
        email: string;
      };
      createdAt: string;
    }>;
    pagination: {
      limit: number;
      skip: number;
      total: number;
    };
  }> {
    let url = `${this.config.baseUrl}/admin/settings/history?limit=${limit}&skip=${skip}`;
    if (settingName) {
      url += `&setting=${settingName}`;
    }
    
    const response = await fetch(url, {
      headers: this.config.headers
    });
    return handleResponse<{
      history: Array<{
        _id: string;
        settingName: string;
        action: 'create' | 'update' | 'delete' | 'reset';
        previousValue?: any;
        newValue?: any;
        category?: string;
        updatedBy?: string;
        user?: {
          _id: string;
          name: string;
          email: string;
        };
        createdAt: string;
      }>;
      pagination: {
        limit: number;
        skip: number;
        total: number;
      };
    }>(response);
  }

  // Get public settings (accessible without authentication)
  async getPublicSettings(): Promise<Record<string, any>> {
    const response = await fetch(`${this.config.baseUrl}/public-settings`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return handleResponse<Record<string, any>>(response);
  }
}